import { Controller } from "@hotwired/stimulus"
import "jquery"
import FundsGridTable from "../../../../../javascript/fundsGridTable"
import {
  integerFormatter,
  currencyFormatter,
  currency3Formatter,
  decimal0Formatter,
  decimal1Formatter,
  decimal2Formatter,
  decimal3Formatter,
  percent1Formatter,
  percent2Formatter,
  percent3Formatter,
  dateFormatter,
  isinFormatter,
} from "../../../../../javascript/utils/formatters"

export default class extends Controller {
  static values = {
    funds: Array,
    selectedColumns: Array,
    columnDefinitions: Array,
    sortColumn: String,
    sortDirection: String
  }

  filterEventListeners = new Map()
  debouncedFilterSubmit = null
  isSubmitting = false

  connect() {
    console.log('Fund table controller connected!')
    console.log('Funds value type:', typeof this.fundsValue)
    console.log('Funds value:', this.fundsValue)
    console.log('Funds value length:', Array.isArray(this.fundsValue) ? this.fundsValue.length : 'not an array')
    if (Array.isArray(this.fundsValue) && this.fundsValue.length > 0) {
      console.log('First fund structure:', Object.keys(this.fundsValue[0]))
      console.log('First fund slug:', this.fundsValue[0].slug, this.fundsValue[0]['slug'])
    }
    console.log('Column definitions value:', this.columnDefinitionsValue)
    this.renderTable()
    this.setupSearch()
    this.setupCsvExport()
    // Re-attach drawer filter listeners whenever this controller connects (e.g. after
    // turbo frame replace from a filter submit), so changing filters keeps working.
    this.initializeAutoSubmit()
    document.addEventListener('fund-drawer-opened', this.boundInitializeAutoSubmit = () => this.initializeAutoSubmit())
    document.addEventListener('fund-reset-filters', this.boundHandleDynamicFilterReset = () => this.handleDynamicFilterReset())
  }

  disconnect() {
    document.removeEventListener('fund-drawer-opened', this.boundInitializeAutoSubmit)
    document.removeEventListener('fund-reset-filters', this.boundHandleDynamicFilterReset)
    if (this.boundCsvExportClick) {
      this.element.removeEventListener('click', this.boundCsvExportClick)
    }
    this.removeExistingListeners()
  }

  setupCsvExport() {
    this.boundCsvExportClick = (e) => {
      if (e.target.closest('.slickgrid-to-csv')) {
        e.preventDefault()
        this.exportSlickGridToCSV()
      }
    }
    this.element.addEventListener('click', this.boundCsvExportClick)
  }

  exportSlickGridToCSV() {
    if (!this.fundsGridTable?.grid || !this.fundsGridTable?.dataView) return
    const dataView = this.fundsGridTable.dataView
    const rows = []
    for (let i = 0; i < dataView.getLength(); i++) {
      rows.push(dataView.getItem(i))
    }
    const csvContent = this.convertRowsToCsv(rows)
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'funds.csv'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  convertRowsToCsv(rows) {
    const colDelim = ','
    const rowDelim = '\r\n'
    const visibleColumns = this.fundsGridTable.grid.getColumns()
    const header = visibleColumns.map((col) => this.escapeCsvCell(col.name)).join(colDelim) + rowDelim
    const dataRows = rows.map((row) =>
      visibleColumns.map((col) => {
        const key = col.field || col.id
        const value = row[key] ?? row[key?.replace(/_/g, '-')] ?? ''
        const raw = value !== null && value !== undefined && typeof value === 'object' && !(value instanceof Date)
          ? (value.text || value.url || '')
          : String(value)
        return this.escapeCsvCell(raw)
      }).join(colDelim)
    )
    return header + dataRows.join(rowDelim)
  }

  escapeCsvCell(value) {
    const s = String(value ?? '')
    if (s.includes(',') || s.includes('"') || s.includes('\r') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }

  setupSearch() {
    const searchInput = document.getElementById('fund-table-search')
    if (searchInput) {
      // Use input event to trigger search as user types
      // This will be called after renderTable() sets up fundsGridTable
      searchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value
        // Trigger search in the table
        if (this.fundsGridTable) {
          this.fundsGridTable.onSearch(searchTerm)
        }
      })
    }
  }

  onSearch(searchTerm) {
    if (this.fundsGridTable) {
      this.fundsGridTable.onSearch(searchTerm)
    }
  }

  renderTable() {
    // Parse funds if it's a JSON string
    let funds = this.fundsValue || []
    if (typeof funds === 'string') {
      try {
        funds = JSON.parse(funds)
      } catch (e) {
        console.error('Error parsing funds JSON:', e)
        funds = []
      }
    }
    
    // Parse columns and selectedColumns if they're JSON strings
    let columns = this.columnDefinitionsValue || []
    if (typeof columns === 'string') {
      try {
        columns = JSON.parse(columns)
      } catch (e) {
        console.error('Error parsing columnDefinitions JSON:', e)
        columns = []
      }
    }
    
    let selectedColumns = this.selectedColumnsValue || []
    if (typeof selectedColumns === 'string') {
      try {
        selectedColumns = JSON.parse(selectedColumns)
      } catch (e) {
        console.error('Error parsing selectedColumns JSON:', e)
        selectedColumns = []
      }
    }
    
    // Calculate grid height with minimum 10 rows and maximum viewport space
    const rowHeight = 32; // From SlickGrid options
    const rowHeaderHeight = 30; // Approximate header height
    const pageHeaderHeight = 250; // Approximate page header height

    // Minimum height: 10 rows
    const minHeight = (10 * rowHeight) + rowHeaderHeight;

    // Maximum height: estimated remaining viewport space
    const viewportHeight = window.innerHeight;
    const maxHeight = viewportHeight - pageHeaderHeight;

    // Use the larger of min and max to ensure we see the most rows
    const calculatedHeight = Math.max(minHeight, maxHeight);

    // Convert string formatter names to actual formatter functions
    const columnDefinitions = columns.map(function (column) {
      // Create a copy of the column to avoid modifying the original
      const newColumn = { ...column };

      // If column id is "isin" or "identifier", automatically apply the ISIN formatter
      if (newColumn.id === "isin" || newColumn.id === "identifier") {
        newColumn.formatter = isinFormatter;
      }
      // If formatter exists as a string, convert it to the actual function
      else if (newColumn.formatter && typeof newColumn.formatter === "string") {
        switch (newColumn.formatter) {
          case "integer":
            newColumn.formatter = integerFormatter;
            break;
          case "currency":
            newColumn.formatter = currencyFormatter;
            break;
          case "currency3":
            newColumn.formatter = currency3Formatter;
            break;
          case "currency0":
            newColumn.formatter = currencyFormatter; // Use currency formatter for currency0
            break;
          case "decimal0":
            newColumn.formatter = decimal0Formatter;
            break;
          case "decimal1":
            newColumn.formatter = decimal1Formatter;
            break;
          case "decimal2":
            newColumn.formatter = decimal2Formatter;
            break;
          case "decimal3":
            newColumn.formatter = decimal3Formatter;
            break;
          case "percent":
            newColumn.formatter = percent2Formatter; // Use percent2 for percent
            break;
          case "percent1":
            newColumn.formatter = percent1Formatter;
            break;
          case "percent2":
            newColumn.formatter = percent2Formatter;
            break;
          case "percent3":
            newColumn.formatter = percent3Formatter;
            break;
          case "date":
            newColumn.formatter = dateFormatter;
            break;
          case "research-report":
            // Special formatter for research report links
            newColumn.formatter = (row, cell, value, columnDef, dataContext) => {
              if (!value || typeof value !== 'object') return '';
              if (value.url) {
                const text = value.text || 'View Report';
                return `<a href="${value.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
              }
              return '';
            };
            break;
          case "research":
            // Special formatter for Product Research (Research Library) links - link text is published date
            newColumn.formatter = (row, cell, value, columnDef, dataContext) => {
              if (!value || typeof value !== 'object') return '';
              if (value.url) {
                const text = value.text || '';
                return `<a href="${value.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
              }
              return '';
            };
            break;
          default:
            // If formatter string doesn't match, remove it to avoid errors
            delete newColumn.formatter;
        }
      }
      
      // Special handling for research-report column
      if (newColumn.id === 'research-report' && !newColumn.formatter) {
        newColumn.formatter = (row, cell, value, columnDef, dataContext) => {
          // Try both field name formats
          const researchReport = value || dataContext?.research_report || dataContext?.['research-report'] || dataContext?.['research_report'];
          if (researchReport && typeof researchReport === 'object' && researchReport.url) {
            const text = researchReport.text || 'View Report';
            return `<a href="${researchReport.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return '';
        };
      }

      // Special handling for research column (Product Research link with date text)
      if (newColumn.id === 'research' && !newColumn.formatter) {
        newColumn.formatter = (row, cell, value, columnDef, dataContext) => {
          const research = value || dataContext?.research || dataContext?.['research'];
          if (research && typeof research === 'object' && research.url) {
            const text = research.text || '';
            return `<a href="${research.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline cursor-pointer">${text}</a>`;
          }
          return '';
        };
      }

      // Preserve the original CSS class and append ba-slick-cell
      newColumn.cssClass = newColumn.cssClass ? `${newColumn.cssClass} ba-slick-cell` : "ba-slick-cell";
      newColumn.headerCssClass = newColumn.headerCssClass || newColumn.cssClass || 'text-center';

      return newColumn;
    });

    // Columns to be displayed in the table
    const processedColumns = [];
    selectedColumns.forEach((col) => {
      const column = columnDefinitions.find((e) => e.id === col);
      if (column) {
        processedColumns.push({
          ...column,
          minWidth: column.width * 1.3
        });
      }
    });

    const options = {
      frozenColumn: 1, // Pin the first column to the left
      editable: false,
      enableAddRow: false,
      enableCellNavigation: true,
      asyncEditorLoading: true,
      autoEdit: false,
      enableAsyncPostRender: true,
      syncColumnCellResize: true,
      autoHeight: false, // Disable autoHeight to use calculated height
      enableAutoResize: true,
      rowHeight: rowHeight,
      enableResponsiveResize: true
    };

    // Set the grid container height
    const gridContainer = document.getElementById("fund-table");
    if (gridContainer) {
      gridContainer.style.height = `${calculatedHeight}px`;
    }

    try {
      // Create FundsGridTable instance - tailored for funds
      this.fundsGridTable = new FundsGridTable({
        data: funds,
        columnDefinitions: processedColumns,
        sortDefault: {
          column: this.sortColumnValue || 'name',
          direction: this.sortDirectionValue || 'asc'
        },
        selectedColumns: selectedColumns,
        tableTarget: "fund-table",
        options: options,
        responsiveOptions: {
          rowHeight: rowHeight,
          minVisibleRows: 10,
          rowHeaderHeight: 30,
          headerOffsetPx: 250
        },
        onSort: (e, args) => {
          // Handle sort if needed
          console.log('Fund table sorted:', args)
        }
      })

      this.grid = this.fundsGridTable.grid
      this.dataView = this.fundsGridTable.dataView

      // Setup search input listener after table is rendered
      // This ensures fundsGridTable is available
      const searchInput = document.getElementById('fund-table-search')
      if (searchInput && this.fundsGridTable) {
        // Remove any existing listeners by cloning the element
        const newSearchInput = searchInput.cloneNode(true)
        searchInput.parentNode.replaceChild(newSearchInput, searchInput)
        
        // Add the search listener that triggers on input
        newSearchInput.addEventListener('input', (e) => {
          if (this.fundsGridTable) {
            this.fundsGridTable.onSearch(e.target.value)
          }
        })
      }
    } catch (error) {
      console.error('Error rendering fund table:', error)
      const gridContainer = document.getElementById('fund-table')
      if (gridContainer) {
        gridContainer.innerHTML = '<div class="p-4 text-center text-neutral-500">Error loading funds table. Please refresh the page.</div>'
      }
    }
  }

  toggleSearch() {
    const searchInput = document.getElementById("fund-table-search")
    const clearButton = document.getElementById("fund-table-search-clear")

    if (searchInput) {
      if (searchInput.classList.contains('w-0')) {
        searchInput.classList.remove('w-0', 'opacity-0', 'pointer-events-none')
        searchInput.classList.add('w-64', 'opacity-100')
        searchInput.focus()

        // Show clear button when search is expanded
        if (clearButton) {
          clearButton.classList.remove('w-0', 'opacity-0', 'pointer-events-none')
          clearButton.classList.add('opacity-100')
        }
      } else {
        searchInput.classList.remove('w-64', 'opacity-100')
        searchInput.classList.add('w-0', 'opacity-0', 'pointer-events-none')

        // Hide clear button when search is collapsed
        if (clearButton) {
          clearButton.classList.remove('opacity-100')
          clearButton.classList.add('w-0', 'opacity-0', 'pointer-events-none')
        }
      }
    }
  }

  clearSearch() {
    const searchInput = document.getElementById("fund-table-search")
    const clearButton = document.getElementById("fund-table-search-clear")
    
    if (searchInput) {
      searchInput.value = ''
      
      // Clear the search in the table
      if (this.fundsGridTable) {
        this.fundsGridTable.onSearch('')
      }
      
      // Collapse the search bar back to just the icon
      searchInput.classList.remove('w-64', 'opacity-100')
      searchInput.classList.add('w-0', 'opacity-0', 'pointer-events-none')
      
      // Hide clear button
      if (clearButton) {
        clearButton.classList.remove('opacity-100')
        clearButton.classList.add('w-0', 'opacity-0', 'pointer-events-none')
      }
    }
  }

  openTableSettingsModal(event) {
    event.preventDefault()

    const modal = document.getElementById('fund-adjust-settings-modal')
    if (modal) {
      // Use the modal component's open method if available
      const modalController = this.application.getControllerForElementAndIdentifier(modal, 'uikit--modal')
      if (modalController) {
        modalController.open()
      } else {
        // Fallback: manually show the modal
        modal.classList.remove('hidden')
        modal.setAttribute('aria-hidden', 'false')
      }
    }
  }

  submitAdjustSettingsForm(event) {
    event.preventDefault()

    const form = document.getElementById('funds-adjust-settings-form')
    if (!form) return

    // requestSubmit() fires the submit event so Turbo can intercept; form.submit() does not
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  closeTableSettingsModal() {
    const modal = document.getElementById('fund-adjust-settings-modal')
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, 'uikit--modal')
      if (modalController) {
        modalController.close()
      } else {
        modal.classList.add('hidden')
        modal.setAttribute('aria-hidden', 'true')
      }
    }
  }

  submitForm() {
    const form = document.getElementById('funds-form')
    if (!form) return

    // Only include filter (q), and sort params when non-default. Exclude
    // authenticity_token, columns[], and any other stray params.
    const DEFAULT_SORT_COLUMN = 'name'
    const DEFAULT_SORT_DIRECTION = 'asc'
    const params = new URLSearchParams()
    const formData = new FormData(form)
    for (const [key, value] of formData.entries()) {
      if (value === '' || value === null) continue
      const trimmed = String(value).trim()
      if (!trimmed) continue
      if (key === 'sort_column') {
        if (trimmed !== DEFAULT_SORT_COLUMN) params.append(key, trimmed)
      } else if (key === 'sort_direction') {
        if (trimmed !== DEFAULT_SORT_DIRECTION) params.append(key, trimmed)
      } else if (key.startsWith('q[')) {
        params.append(key, trimmed)
      }
    }

    const queryString = params.toString()

    // Update address bar to page URL with only allowed params (e.g. /funds?q[...]&sort_column=...)
    const pageUrl = queryString !== ''
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname
    history.replaceState({}, '', pageUrl)

    // Fetch the frame from the funds_data endpoint with the same params
    const frameUrl = queryString !== ''
      ? `${(form.action || '').split('?')[0]}?${queryString}`
      : (form.action || '').split('?')[0]
    if (frameUrl && typeof Turbo !== 'undefined' && Turbo.visit) {
      Turbo.visit(frameUrl, { frame: 'funds_data' })
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
    }
  }

  handleDynamicFilterSubmit() {
    const dynamicFiltersContainer = document.getElementById('fund-drawer')
    if (!dynamicFiltersContainer?.dataset.filterMapping) return

    const FILTER_MAPPING = JSON.parse(dynamicFiltersContainer.dataset.filterMapping || '{}')
    const AUTO_ELEMENTS = Object.keys(FILTER_MAPPING)
    const form = document.getElementById('funds-form')
    if (!form) return

    form.querySelectorAll('input[name^="q["][name*="_eq"], input[name^="q["][name*="_gteq"], input[name^="q["][name*="_lteq"], input[name^="q["][name*="_in"]').forEach((input) => input.remove())

    AUTO_ELEMENTS.forEach((field) => {
      const filterConfig = FILTER_MAPPING[field]
      const filterField = filterConfig?.field || filterConfig?.column
      const filterType = filterConfig?.filter_type

      if (!filterField) return

      switch (filterType) {
        case 'Decimal Range':
        case 'Date Range': {
          const minInput = document.getElementById(`min-${field}`)
          const maxInput = document.getElementById(`max-${field}`)
          if (minInput?.value?.trim()) {
            const hi = document.createElement('input')
            hi.type = 'hidden'
            hi.name = `q[${filterField}_gteq]`
            hi.value = minInput.value
            form.appendChild(hi)
          }
          if (maxInput?.value?.trim()) {
            const hi = document.createElement('input')
            hi.type = 'hidden'
            hi.name = `q[${filterField}_lteq]`
            hi.value = maxInput.value
            form.appendChild(hi)
          }
          break
        }
        case 'Multi Select': {
          const inputs = document.querySelectorAll(`input[name="q[${filterField}_in][]"]`)
          const values = []
          inputs.forEach((input) => {
            if (input.value?.trim()) values.push(input.value.trim())
          })
          values.forEach((value) => {
            const hi = document.createElement('input')
            hi.type = 'hidden'
            hi.name = `q[${filterField}_in][]`
            hi.value = value
            form.appendChild(hi)
          })
          break
        }
        default:
          break
      }
    })

    this.submitForm()
  }

  handleFilterChange() {
    if (this.isSubmitting) return
    if (this.debouncedFilterSubmit) clearTimeout(this.debouncedFilterSubmit)
    this.debouncedFilterSubmit = setTimeout(() => {
      this.isSubmitting = true
      this.handleDynamicFilterSubmit()
      setTimeout(() => { this.isSubmitting = false }, 100)
    }, 500)
  }

  removeExistingListeners() {
    this.filterEventListeners.forEach((listener, element) => {
      element.removeEventListener('input', listener)
      element.removeEventListener('change', listener)
    })
    this.filterEventListeners.clear()
  }

  initializeAutoSubmit() {
    this.removeExistingListeners()
    const dynamicFiltersContainer = document.getElementById('fund-drawer')
    if (!dynamicFiltersContainer?.dataset.filterMapping) return

    const FILTER_MAPPING = JSON.parse(dynamicFiltersContainer.dataset.filterMapping || '{}')
    const AUTO_ELEMENTS = Object.keys(FILTER_MAPPING)

    AUTO_ELEMENTS.forEach((field) => {
      const filterConfig = FILTER_MAPPING[field]
      const filterField = filterConfig?.field || filterConfig?.column
      const filterType = filterConfig?.filter_type

      switch (filterType) {
        case 'Decimal Range':
        case 'Date Range': {
          const minInput = document.getElementById(`min-${field}`)
          const maxInput = document.getElementById(`max-${field}`)
          if (minInput) {
            const listener = () => this.handleFilterChange()
            minInput.addEventListener('input', listener)
            this.filterEventListeners.set(minInput, listener)
          }
          if (maxInput) {
            const listener = () => this.handleFilterChange()
            maxInput.addEventListener('input', listener)
            this.filterEventListeners.set(maxInput, listener)
          }
          break
        }
        case 'Multi Select': {
          const multiSelectInput = document.querySelector(`input[name="q[${filterField}_in][]"][data-main-input="true"]`) || document.querySelector(`input[name="q[${filterField}_in][]"]`)
          if (multiSelectInput) {
            const listener = () => this.handleFilterChange()
            multiSelectInput.addEventListener('change', listener)
            this.filterEventListeners.set(multiSelectInput, listener)
          }
          break
        }
        default:
          break
      }
    })
  }

  handleDynamicFilterReset() {
    const drawer = document.getElementById('fund-drawer')
    if (drawer) {
      drawer.querySelectorAll('input, select, textarea').forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false
        } else {
          input.value = ''
        }
      })

      const multiSelectComponents = drawer.querySelectorAll('[data-controller="uikit--select-multi"]')
      multiSelectComponents.forEach((component) => {
        try {
          const controller = this.application.getControllerForElementAndIdentifier(component, 'uikit--select-multi')
          if (controller && typeof controller.clear === 'function') {
            controller.clear()
          }
        } catch (_e) {
          // ignore
        }
      })
    }

    history.replaceState({}, '', window.location.pathname)
    this.submitForm()
  }

}

